import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthPage } from './components/auth/AuthPage';
import { useAuthStore } from './stores/authStore';
import { Sidebar } from './components/layout/Sidebar';
import { Dashboard } from './components/dashboard/Dashboard';
import { Bell, Search, User, Moon, Sun, Menu, CheckCircle, CalendarClock, Info, AlertTriangle } from 'lucide-react';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { ApplicationForm } from './components/applications/ApplicationForm';
import { ApplicationList } from './components/applications/ApplicationList';
import { InterviewCalendar } from './components/interviews/InterviewCalendar';
import { NotificationManager } from "./components/notifications";
import { useNotificationStore } from "./stores/notificationStore";
// import { useDemoNotificationTrigger } from "./hooks/useDemoNotificationTrigger"; // COMMENTATO
import { clsx } from 'clsx';
import { ApplicationDetail } from './components/applications/ApplicationDetail';
import { useApplicationStore } from './stores/applicationStore';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadScript } from '@react-google-maps/api';
import { UserProfilePage } from './components/profile/UserProfilePage';
import { SettingsPage } from './components/settings/SettingsPage';
import { useApplicationReminders } from './hooks/useApplicationReminders';
import { useSidebar } from './contexts/SidebarContext';
import { SidebarProvider } from './contexts/SidebarContext';
import './utils/faqCleanup'; // Importa le utility per la pulizia FAQ
import { MainLayout } from './components/layout/MainLayout';
import { AdminLogin } from './components/auth';
import { QuoVadisLogo } from './components/ui/QuoVadisLogo';
import { DocumentsPage } from './components/documents/DocumentsPage';
import { DrivePage } from './components/drive/DrivePage';
import ModeratorDashboard from './components/moderator/ModeratorDashboard';
import AllApplicationsPage from './components/moderator/AllApplicationsPage';
import MyContactsPage from './components/moderator/MyContactsPage';
import AddContactModal from './components/moderator/AddContactModal'; // Still imported but not directly used in App.tsx
import { EditApplicationPage } from './components/applications/EditApplicationPage';
import { ViewApplicationPage } from './components/applications/ViewApplicationPage';
import FAQPage from './components/faq/FAQPage';
import FAQManagementPage from './components/admin/FAQManagementPage';
import AdvancedReportPage from './components/moderator/AdvancedReportPage';
import InterviewManagementPage from './components/moderator/InterviewManagementPage';
import AddInterviewPage from './components/moderator/AddInterviewPage';
import { HelpPage } from './components/help/HelpPage';
import { DashboardHelpPage } from './components/help/DashboardHelpPage';
import { ApplicationsHelpPage } from './components/help/ApplicationsHelpPage';
import { CalendarHelpPage } from './components/help/CalendarHelpPage';
import { DriveHelpPage } from './components/help/DriveHelpPage';
import { SettingsHelpPage } from './components/help/SettingsHelpPage';

// Componente che mostra la dashboard corretta in base al ruolo
const RoleBasedDashboard: React.FC = () => {
  const { userProfile } = useAuthStore();
  
  if (userProfile?.role === 'moderator') {
    return <ModeratorDashboard />;
  }
  
  if (userProfile?.role === 'admin') {
    return <AdminDashboard />;
  }
  
  return <Dashboard />;
};

// Header Component
const Header: React.FC<{ currentPage: string }> = ({ currentPage }) => {
  const { signOut } = useAuth();
  const { userProfile } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);

  // Chiudi menu quando si clicca fuori
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (userMenuOpen && !target.closest('.user-menu')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);
  const notifications = useNotificationStore((s) => s.notifications);
  const dismissNotification = useNotificationStore((s) => s.dismissNotification);
  const clearNotifications = useNotificationStore((s) => s.clearNotifications);
  const navigate = useNavigate();

  const getPageTitle = (page: string) => {
    const titles = {
      'dashboard': 'Dashboard',
      'moderator': 'Gestione Candidature',
      'moderator-applications': 'Tutte le Candidature',
      'moderator-contacts': 'I Miei Contatti',
      'moderator-report': 'Report Avanzato',
      'moderator-interviews': 'Gestione Interviste',
      'applications': 'Candidature',
      'interviews': 'Colloqui',
      'statistics': 'Statistiche',
      'contacts': 'Contatti',
      'documents': 'Documenti',
      'drive': 'Drive',
      'help': 'Come Funziona?',
      'settings': 'Impostazioni',
      'profile': 'Profilo Utente',
      'faq': 'Domande Frequenti',
      'faq-management': 'Gestione FAQ'
    };
    return titles[page as keyof typeof titles] || 'Dashboard';
  };

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        {/* Left section: Logo and title */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="block sm:hidden focus:outline-none"
            onClick={() => navigate('/dashboard')}
            aria-label="Vai alla dashboard"
            style={{ background: 'none', border: 'none', padding: 0 }}
          >
            <QuoVadisLogo size="xs" className="block sm:hidden" />
          </button>
          <div className="hidden sm:block">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {getPageTitle(currentPage)}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              {currentPage === 'dashboard' ?
                (userProfile?.role === 'moderator' ? 'Panoramica candidature di tutti gli utenti' : 'Panoramica delle tue candidature') :
               currentPage === 'moderator' ? 'Gestione candidature di tutti gli utenti' :
               currentPage === 'moderator-applications' ? 'Lista completa di tutte le candidature' :
               currentPage === 'moderator-contacts' ? 'Database completo dei tuoi contatti con aziende' :
               currentPage === 'faq' ? 'Trova risposte alle domande più comuni' :
               currentPage === 'faq-management' ? 'Crea e gestisci le domande frequenti' :
               `Gestisci le tue ${getPageTitle(currentPage).toLowerCase()}`}
            </p>
          </div>
        </div>

        {/* Right section: notifications, user, sidebar toggle */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Sidebar toggle button (desktop only) */}
          <button
            onClick={toggleSidebar}
            className="hidden sm:flex p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 rounded-md transition-colors"
            aria-label={`${isSidebarOpen ? 'Nascondi' : 'Mostra'} sidebar`}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Notifications dropdown - TEMPORANEAMENTE NASCOSTO */}
          {/* 
          <div className="relative">
            <button
              className="relative p-2 text-gray-400 hover:text-gray-600 rounded-md"
              aria-label="Notifiche"
              onClick={() => setNotifOpen((v) => !v)}
            >
              <Bell className="h-5 w-5" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 max-w-xs bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-2 animate-fade-in-up pointer-events-auto sm:block sm:relative sm:mt-2 sm:w-80 sm:max-w-xs sm:bg-white sm:border sm:border-gray-200 sm:rounded-xl sm:shadow-lg sm:z-50 sm:py-2 sm:animate-fade-in-up sm:pointer-events-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setNotifOpen(false)} />
                <div className="fixed inset-0 z-50 bg-white sm:relative sm:inset-auto sm:bg-transparent sm:z-auto">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 sm:hidden">
                    <button
                      onClick={() => setNotifOpen(false)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">Notifiche</h3>
                    <div className="w-10 h-10"></div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto sm:max-h-96">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-400 text-sm">Nessuna notifica</div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-3 px-4 py-2 border-b border-gray-100 sm:border-b-0 sm:mb-1 sm:px-2">
                          <span className="text-sm font-medium text-gray-700 sm:hidden">
                            {notifications.length} notifica{notifications.length !== 1 ? 'he' : ''}
                          </span>
                          <button
                            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg shadow transition-colors sm:px-2 sm:py-1 sm:text-xs"
                            onClick={clearNotifications}
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span className="sm:hidden">Segna tutte come lette</span>
                            <span className="hidden sm:inline">Tutte lette</span>
                          </button>
                        </div>
                        <div className="px-4 sm:px-2">
                          {notifications.map((n) => (
                            <div
                              key={n.id}
                              className={clsx(
                                "relative flex items-start gap-3 bg-white border-l-4 shadow rounded-xl px-4 py-4 mb-3 sm:py-3 sm:mb-1 sm:px-4",
                                n.type === "reminder" && "border-blue-500",
                                n.type === "info" && "border-sky-500",
                                n.type === "warning" && "border-yellow-500"
                              )}
                            >
                              <div className="pt-1">
                                {n.type === "reminder" && <CalendarClock className="w-5 h-5 text-blue-500 shrink-0" />}
                                {n.type === "info" && <Info className="w-5 h-5 text-sky-500 shrink-0" />}
                                {n.type === "warning" && <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm flex items-center gap-2 sm:text-sm">
                                  {n.demo && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">DEMO</span>}
                                  {n.title}
                                </div>
                                <div className="text-sm text-gray-700 mt-1 break-words leading-snug sm:text-xs">
                                  {n.message}
                                </div>
                                <div className="text-xs text-gray-400 mt-2 sm:hidden">
                                  {new Date(n.timestamp).toLocaleString('it-IT')}
                                </div>
                              </div>
                              <button
                                className="ml-2 text-gray-400 hover:text-gray-700 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-200 rounded transition-colors duration-150"
                                onClick={() => dismissNotification(n.id)}
                                aria-label="Chiudi notifica"
                                tabIndex={0}
                                style={{ WebkitTapHighlightColor: "transparent" }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          */}

          {/* User menu dropdown */}
          <div className="relative user-menu">
            <button
              className="flex items-center space-x-2 sm:space-x-3 p-2 text-gray-700 hover:text-gray-900 rounded-md focus:outline-none"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-label="Profilo utente"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium">{userProfile?.name || 'Utente'}</p>
                <p className="text-xs text-gray-500">
                  {userProfile?.role === 'admin' ? '👑 Admin' : 
                   userProfile?.role === 'moderator' ? '🛡️ Moderatore' : 'Utente'}
                </p>
              </div>
            </button>
            {/* Dropdown menu */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2 animate-fade-in-up">
                <div className="px-4 py-2 text-sm text-gray-700 font-medium border-b border-gray-100">
                  {userProfile?.name || 'Utente'}
                </div>
                <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                  {userProfile?.role === 'admin' ? '👑 Admin' : 
                   userProfile?.role === 'moderator' ? '🛡️ Moderatore' : 'Utente'}
                </div>
                <button
                  onClick={() => {
                    navigate('/settings');
                    setUserMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  ⚙️ Impostazioni
                </button>
                <a
                  href="https://wa.me/410912265254"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 block"
                >
                  💬 Contattaci
                </a>
                <button
                  onClick={signOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu hamburger - positioned lower to avoid overlap */}
          <button
            onClick={toggleSidebar}
            className="block sm:hidden p-1.5 text-gray-400 hover:text-gray-600 rounded-md transition-colors"
            aria-label={`${isSidebarOpen ? 'Nascondi' : 'Mostra'} sidebar`}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Logout button only on sm+ (mobile: nel menu) */}
          <button
            onClick={signOut}
            className="btn-secondary text-sm hidden sm:inline-block"
          >
            Logout
          </button>
        </div>
      </div>
      <style>{`
        @media (max-width: 640px) {
          .xs\\:block { display: block !important; }
          .xs\\:hidden { display: none !important; }
        }
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.25s cubic-bezier(.4,0,.2,1);
        }
      `}</style>
    </header>
  );
};

export { Header };

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, error } = useAuthStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento autenticazione...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-red-800 font-medium">Errore di autenticazione</h3>
            <p className="text-red-600 text-sm mt-2">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 btn-primary"
            >
              Riprova
            </button>
          </div>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/auth" replace />;
};

// Public Route Component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuthStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifica autenticazione...</p>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

function ApplicationDetailPage() {
  const { id } = useParams();
  const { applications, deleteApplication } = useApplicationStore();
  const navigate = useNavigate();
  const app = applications.find(a => a.id === id) || null;
  const [confirm, setConfirm] = React.useState(false);
  if (!app) return <div className="p-8 text-center text-gray-400">Candidatura non trovata</div>;
  return (
    <div className="max-w-lg mx-auto p-4 sm:p-8">
      <button className="mb-4 text-blue-600 font-semibold" onClick={() => navigate('/applications')}>&larr; Torna indietro</button>
      <ApplicationDetail application={app} onClose={() => navigate('/applications')} />
      <div className="flex gap-2 mt-6">
        <button className="flex-1 bg-blue-600 text-white rounded-lg py-2 font-semibold shadow hover:bg-blue-700 transition" onClick={() => alert('TODO: Modifica candidatura')}>Modifica</button>
        <button className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold shadow hover:bg-red-700 transition" onClick={() => setConfirm(true)}>Elimina</button>
      </div>
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-xs w-full text-center">
            <h3 className="text-lg font-bold mb-2 text-red-700">Sei sicuro?</h3>
            <p className="text-gray-600 mb-4">Questa azione non può essere annullata.<br/>Vuoi eliminare la candidatura?</p>
            <div className="flex gap-2 mt-2">
              <button className="flex-1 bg-gray-100 rounded-lg py-2 font-semibold" onClick={() => setConfirm(false)}>Annulla</button>
              <button className="flex-1 bg-red-600 text-white rounded-lg py-2 font-semibold shadow hover:bg-red-700 transition" onClick={() => { deleteApplication(app.id); navigate('/applications'); }}>Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Costante per le librerie Google Maps per evitare warning di performance
const GOOGLE_MAPS_LIBRARIES: ("places" | "geometry" | "drawing" | "visualization")[] = ["places"];

function App() {
  const notifications = useNotificationStore((s) => s.notifications);
  const dismissNotification = useNotificationStore((s) => s.dismissNotification);
  const clearNotifications = useNotificationStore((s) => s.clearNotifications);
  // useDemoNotificationTrigger(); // COMMENTATO
  
  // Inizializza i reminder automatici per le candidature
  useApplicationReminders();
  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
      libraries={GOOGLE_MAPS_LIBRARIES}
      loadingElement={<div className="min-h-screen flex items-center justify-center">Caricamento Google Maps...</div>}
    >
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <SidebarProvider>
              <NotificationManager notifications={notifications} onDismiss={dismissNotification} onClearAll={clearNotifications} />
              <div className="App">
                <Routes>
                  <Route path="/admin-login" element={<AdminLogin />} />
                  <Route 
                    path="/auth" 
                    element={
                      <PublicRoute>
                        <AuthPage />
                      </PublicRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="dashboard">
                          <RoleBasedDashboard />
                        </MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/applications" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="applications">
                          <ApplicationList />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/applications/new" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="applications">
                          <ApplicationForm />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/calendar" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="calendar">
                          <InterviewCalendar />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/applications/:id/edit" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="applications">
                          <EditApplicationPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/applications/:id" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="applications">
                          <ViewApplicationPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/profile" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="profile">
                          <UserProfilePage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="settings">
                          <SettingsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/admin" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="admin">
                          <AdminDashboard />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/documents" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="documents">
                          <DocumentsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/drive" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="drive">
                          <DrivePage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/moderator/applications" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="moderator-applications">
                          <AllApplicationsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/moderator/contacts" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="moderator-contacts">
                          <MyContactsPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/moderator/report" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="moderator-report">
                          <AdvancedReportPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/moderator/interviews" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="moderator-interviews">
                          <InterviewManagementPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/moderator/interviews/new" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="moderator-interviews">
                          <AddInterviewPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/help" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="help">
                          <HelpPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/help/dashboard" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="help">
                          <DashboardHelpPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/help/applications" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="help">
                          <ApplicationsHelpPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/help/calendar" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="help">
                          <CalendarHelpPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/help/drive" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="help">
                          <DriveHelpPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/help/settings" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="help">
                          <SettingsHelpPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/faq" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="faq">
                          <FAQPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/admin/faq" 
                    element={
                      <ProtectedRoute>
                        <MainLayout currentPage="faq-management">
                          <FAQManagementPage />
                        </MainLayout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </SidebarProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </LoadScript>
  );
}

export default App;
